import Vue from 'vue';
import VueRouter from 'vue-router';

// The four pages nearly every visit lands on stay in the main bundle — asking
// for a separate chunk before they can even start fetching would cost them a
// round trip. Everything else is split off and fetched when its route is hit.
import PageIndex from '~/components/PageIndex.vue';
import PageAddress from '~/components/address/PageAddress.vue';
import PageTx from '~/components/tx/PageTx.vue';
import PageJetton from '~/components/jetton/PageJetton.vue';

import { ADDRESS_REGEX, APP_MAIN_LOCALE, TELEGRAM_USERNAME_REGEX, TON_DOMAIN_REGEX } from '~/config.js';
import { resolveDomainAddress } from '~/search.js';

const PageAddressInvalid = () => import(/* webpackChunkName: "page-address-invalid" */ '~/components/address/PageAddressInvalid.vue');
const PageBlock = () => import(/* webpackChunkName: "page-block" */ '~/components/block/PageBlock.vue');
const PageBlockLatest = () => import(/* webpackChunkName: "page-block-latest" */ '~/components/block/PageBlockLatest.vue');
const PageBlocks = () => import(/* webpackChunkName: "page-blocks" */ '~/components/block/PageBlocks.vue');
const PageNft = () => import(/* webpackChunkName: "page-nft" */ '~/components/nft/PageNft.vue');
const PageNftCollection = () => import(/* webpackChunkName: "page-collection" */ '~/components/nft/PageCollection.vue');
const PagePool = () => import(/* webpackChunkName: "page-pool" */ '~/components/pool/PagePool.vue');
const PageLp = () => import(/* webpackChunkName: "page-lp" */ '~/components/lp/PageLp.vue');
const PageAuction = () => import(/* webpackChunkName: "page-auction" */ '~/components/auction/PageAuction.vue');
const PageStats = () => import(/* webpackChunkName: "page-stats" */ '~/components/stats/PageStats.vue');
const PageVesting = () => import(/* webpackChunkName: "page-vesting" */ '~/components/pool/PageVesting.vue');
const PageMultisig = () => import(/* webpackChunkName: "page-multisig" */ '~/components/multisig/PageMultisig.vue');
const PageMultisigOrder = () => import(/* webpackChunkName: "page-multisig-order" */ '~/components/multisig/PageMultisigOrder.vue');
const PageLocker = () => import(/* webpackChunkName: "page-locker" */ '~/components/address/PageLocker.vue');
const PageValidators = () => import(/* webpackChunkName: "page-validators" */ '~/components/validators/PageValidators.vue');
const PageSuspended = () => import(/* webpackChunkName: "page-suspended" */ '~/components/address/PageSuspended.vue');
const PageMarket = () => import(/* webpackChunkName: "page-market" */ '~/components/market/PageMarket.vue');
const PageWhales = () => import(/* webpackChunkName: "page-whales" */ '~/components/PageWhales.vue');
const PageError = () => import(/* webpackChunkName: "page-error" */ '~/components/PageError.vue');
const PageConfig = () => import(/* webpackChunkName: "page-config" */ '~/components/config/PageConfig.vue');
const PageApps = () => import(/* webpackChunkName: "page-apps" */ '~/components/apps/PageApps.vue');
const AppWrapper = () => import(/* webpackChunkName: "page-apps" */ '~/components/apps/App/AppWrapper.vue');
const PageJettonWallet = () => import(/* webpackChunkName: "page-token-wallet" */ '~/components/jettonWallet/PageJetton.vue');
const PageAdvertise = () => import(/* webpackChunkName: "page-advertise" */ '~/components/advertisement/PageAdvertise.vue');
const PageMcp = () => import(/* webpackChunkName: "page-mcp" */ '~/components/mcp/PageMcp.vue');
const PageMcpPrivacy = () => import(/* webpackChunkName: "page-mcp" */ '~/components/mcp/PageMcpPrivacy.vue');
const PageTools = () => import(/* webpackChunkName: "page-tools" */ '~/components/tools/PageTools.vue');

Vue.use(VueRouter);

// Domain routes (tolya.ton, tolya.t.me) resolve the name and land on the
// address page. An unbound name shows the error page under its own url, so
// the visitor can see what exactly wasn't found:
const redirectDomainToAddress = async (to, from, next) => {
    const domain = to.params.domain;

    try {
        const address = await resolveDomainAddress(domain);

        if (address) {
            next({ name: 'address', params: { address }, replace: true });
            return;
        }
    } catch (err) {
        // resolver is down, treat as not found
    }

    next({ name: '404', params: { pathMatch: domain, domain }, replace: true });
};

const router = new VueRouter({
    hashbang: false,
    mode: 'history',
    /* eslint consistent-return: "off" */
    scrollBehavior(to, from, savedPosition) {
        // Exception for holders hash change event on jetton page
        if ((to.name === 'token' || to.name === 'lp') && to.hash === '#holders') return;
        if (to.name === 'pool' && to.hash === '#stakers') {
            return { selector: '#tab_switcher_stakers' };
        }
        if (to.name === 'apps' && from.name === 'apps') {
            return savedPosition;
        }
        // Switching tools is a hash change on one page: the menu stays where it is.
        if (to.name === 'tools' && from.name === 'tools') return;
        // A link to one card of the stats page (/stats#unlocks) lands on that card, under the sticky header:
        if (to.name === 'stats' && to.hash) {
            return { selector: to.hash, offset: { x: 0, y: 72 } };
        }
        return { x: 0, y: 0 };
    },
    // Production answers 200 only to the paths of this array: the build turns it into
    // _redirects for Cloudflare Pages (scripts/pages-control-files-plugin.js). The script reads
    // this file as text, so paths stay string literals, with regexes as `${SOME_REGEX.source}`.
    /* eslint no-useless-escape: "off" */
    routes: [{
        path: '/:lang(ru|en)?',
        component: {
            render: h => h('router-view'),
        },
        children: [{
            name: 'index',
            path: '/',
            component: PageIndex,
        }, {
            name: 'address',
            path: `address/:address(${ADDRESS_REGEX.source})`,
            component: PageAddress,
            props: true,
        }, {
            name: 'address_invalid',
            path: 'address/:address',
            component: PageAddressInvalid,
            props: true,
        }, {
            // redirect from tonapi format:
            path: `account/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'address' },
        }, {
            name: 'tx_by_msg_hash',
            path: 'tx/by-msg-hash/:hash([^:]+)',
            component: PageTx,
            props: true,
        }, {
            path: 'transaction/:hash([^:$]+)',
            redirect: { name: 'tx' },
        }, {
            // redirect from OKX vsraty format:
            path: 'tx/&lt=:lt([\\d]+)&hash=:hash([^:$]+)',
            redirect: { name: 'tx' },
        }, {
            // redirect from old transaction format (lt:hash:address):
            path: 'tx/:lt([\\d]+)\::hash([^:]+)\::address(.{48})',
            redirect: { name: 'tx' },
        }, {
            // redirect from ancient transaction format (lt$hash$address):
            path: 'tx/:lt([\\d]+)$:hash([^:$]+)$:address(.{48})',
            redirect: { name: 'tx' },
        }, {
            name: 'tx',
            path: 'tx/:hash([^:$]{44,})',
            component: PageTx,
            props: true,
        }, {
            name: 'block',
            path: 'block/:workchain([\\-\\d]+)\::shard([a-fA-F\\d]+)\::seqno([\\d]+)',
            component: PageBlock,
            props: true,
        }, {
            name: 'block_latest',
            path: 'block/latest',
            component: PageBlockLatest,
        }, {
            name: 'blocks',
            path: 'blocks',
            component: PageBlocks,
        }, {
            name: 'nft',
            path: `nft/:address(${ADDRESS_REGEX.source})`,
            component: PageNft,
            props: true,
        }, {
            path: 'nft/:address',
            redirect: { name: 'address' },
        }, {
            name: 'collection',
            path: `collection/:address(${ADDRESS_REGEX.source})`,
            component: PageNftCollection,
            props: true,
        }, {
            name: 'nft_item',
            path: `nft_item/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'nft', replace: true },
        }, {
            name: 'token',
            path: `token/:address(${ADDRESS_REGEX.source})`,
            component: PageJetton,
            props: true,
        }, {
            path: 'token/:address',
            redirect: { name: 'address' },
        }, {
            name: 'lp',
            path: `lp/:address(${ADDRESS_REGEX.source})`,
            component: PageLp,
            props: true,
        }, {
            path: 'lp/:address',
            redirect: { name: 'address' },
        }, {
            // Auctions and fix-price sales share this route: they are two
            // flavors of the same listing contract and render the same card.
            name: 'auction',
            path: `auction/:address(${ADDRESS_REGEX.source})`,
            component: PageAuction,
            props: true,
        }, {
            path: 'auction/:address',
            redirect: { name: 'address' },
        }, {
            // Legacy url, kept for external links and bookmarks. It stays named
            // so addressbook entries saved with the old 'jetton' type still
            // navigate (they land on /token via this redirect):
            name: 'jetton',
            path: `jetton/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'token' },
        }, {
            path: 'jetton/:address',
            redirect: { name: 'address' },
        }, {
            name: 'token-wallet',
            path: `token-wallet/:address(${ADDRESS_REGEX.source})`,
            component: PageJettonWallet,
            props: true,
        }, {
            path: 'token-wallet/:address',
            redirect: { name: 'address' },
        }, {
            // Legacy url, kept for external links and bookmarks:
            name: 'jetton-wallet',
            path: `jetton-wallet/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'token-wallet' },
        }, {
            path: 'jetton-wallet/:address',
            redirect: { name: 'address' },
        }, {
            // Legacy url and the former name of the pool route. It stays named
            // so addressbook entries saved with the 'nominator' type still
            // navigate (they land on /pool via this redirect):
            name: 'nominator',
            path: `nominator/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'pool' },
        }, {
            name: 'pool',
            path: `pool/:address(${ADDRESS_REGEX.source})`,
            component: PagePool,
            props: true,
        }, {
            name: 'vesting',
            path: `vesting/:address(${ADDRESS_REGEX.source})`,
            component: PageVesting,
            props: true,
        }, {
            name: 'multisig',
            path: `multisig/:address(${ADDRESS_REGEX.source})`,
            component: PageMultisig,
            props: true,
        }, {
            name: 'multisig_order',
            path: `multisig-order/:address(${ADDRESS_REGEX.source})`,
            component: PageMultisigOrder,
            props: true,
        }, {
            name: 'locker',
            path: `locker/:address(${ADDRESS_REGEX.source})`,
            component: PageLocker,
            props: true,
        }, {
            // Legacy url, kept for external links and bookmarks. It stays named
            // so addressbook entries saved with the 'single_nominator' type
            // still navigate (they land on /pool via this redirect):
            name: 'single_nominator',
            path: `single-nominator/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'pool' },
        }, {
            name: 'suspended',
            path: 'suspended',
            component: PageSuspended,
        }, {
            name: 'stats',
            path: 'stats',
            component: PageStats,
        }, {
            name: 'validators',
            path: 'validators',
            component: PageValidators,
        },
        {
            name: 'market',
            path: 'market',
            component: PageMarket,
        },
        {
            name: 'apps',
            path: 'apps',
            component: PageApps,
            props: true,
        }, {
            name: 'apps-category',
            path: 'apps/:category',
            component: PageApps,
            props: true,
            children: [{
                name: 'app',
                path: ':app',
                component: AppWrapper,
                props: true,
            }],
        }, {
            name: 'whales',
            path: 'whales',
            component: PageWhales,
        },
        {
            path: 'top',
            redirect: { name: 'whales', replace: true },
        },
        {
            path: 'richlist',
            redirect: { name: 'whales', replace: true },
        },
        {
            name: 'config',
            path: 'config',
            component: PageConfig,
        },
        {
            name: 'advertise',
            path: 'advertise',
            component: PageAdvertise,
        },
        {
            name: 'mcp',
            path: 'mcp',
            component: PageMcp,
        },
        {
            name: 'mcp_privacy',
            path: 'mcp/privacy',
            component: PageMcpPrivacy,
        },
        {
            name: 'tools',
            path: 'tools',
            component: PageTools,
        },
        {
            path: 'ads',
            redirect: { name: 'advertise', replace: true },
        },
        {
            path: `:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'address', replace: true },
        }, {
            name: 'dns_domain',
            path: `:domain(${TON_DOMAIN_REGEX.source})`,
            beforeEnter: redirectDomainToAddress,
        }, {
            name: 'telegram_username',
            path: `:domain(${TELEGRAM_USERNAME_REGEX.source})`,
            beforeEnter: redirectDomainToAddress,
        }, {
            name: '404',
            path: '*',
            component: PageError,
        },
        ],
    }],
});

/* eslint no-unused-vars: "off" */
router.beforeEach((to, from, next) => {
    // replace main locale prefix with domain root:
    if (to.params.lang === APP_MAIN_LOCALE) {
        next({
            ...to,
            params: {
                ...to.params,
                lang: undefined,
            },
        });
    } else {
        next();
    }
});

router.afterEach((to, from) => {
    Vue.nextTick(() => {
        const address = to.params.address;

        if (address) {
            document.title = `${address} · Tonscan`;
        }
    });
});

export default router;
